'use client';

import { useEffect, useEffectEvent, useRef } from 'react';
import { Box, Dialog, DialogContent } from '@mui/material';
import IconButtonWhite from '@/components/formElements/iconButtons/IconButtonWhite';
import styled from '@emotion/styled';
import { breakpoints, color, spacing } from '@/theme/tokens';
import IconClose from '@/assets/icons/IconClose';

type VideoUrl = string;

interface VideoPlayerModalProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly videoUrl: VideoUrl;
    readonly title?: string;
    readonly className?: string;
    readonly 'data-testid'?: string;
}

const Container = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background: transparent;
    box-sizing: border-box;
    overflow: hidden;
    padding: ${spacing[16]};
    
    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[32]};
    }
    
    @media (min-width: ${breakpoints.md}) {
        padding: ${spacing[80]};
    }
`;

const Controls = styled(Box)`
    position: absolute;
    right: -9px;
    top: -40px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    z-index: 1;
    
    @media (min-width: ${breakpoints.md}) {
        position: absolute;
        right: -48px;
        top: -8px;
    }
`;

const CloseButton = styled(IconButtonWhite)`
    width: 40px;
    height: 40px;
    transition: opacity 150ms ease;
    z-index: 9999;
    color: ${color.white[80]};
    &:hover {
        background-color: transparent;
    }
`;

const VideoContainer = styled(Box)`
    position: relative;
    max-width: 1920px;
    max-height: calc(100vh - 160px);
    min-height: 150px;
    width: clamp(370px, calc((100vh - ${spacing[160]}) * 1.78), min(100%, 1920px));
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: ${breakpoints.md}) {
        max-width: 90vw;
        max-height: calc(100vh - ${spacing[64]});
        width: clamp(280px, calc((100vh - ${spacing[64]}) * 1.78), 90vw);
    }

    @media (max-height: 872px) {
        max-height: calc(100vh - 20px);
    }
`;

const VideoElement = styled('video')`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const isVimeoUrl = (url: string): boolean => {
    return url.includes('player.vimeo.com') || url.includes('vimeo.com');
};

const VimeoIframe = styled('iframe')`
    width: 100%;
    height: 100%;
    border: none;
`;

const handleEnded = () => {
    // Video ended
};

export default function VideoPlayerModal({
    open,
    onClose,
    videoUrl,
    title,
    className,
    'data-testid': dataTestId
}: VideoPlayerModalProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const isVimeo = videoUrl ? isVimeoUrl(videoUrl) : false;

    useEffect(() => {
        if (isVimeo) return;

        if (open && videoRef.current) {
            const video = videoRef.current;
            video.load();
        } else if (!open && videoRef.current) {
            const video = videoRef.current;
            video.pause();
            video.currentTime = 0;
        }
    }, [open, isVimeo]);

    // Effect Event: keydown listener sees the latest onClose without re-subscribing on every parent render.
    const closeOnEscape = useEffectEvent(() => {
        onClose();
    });

    useEffect(() => {
        if (!open) return;

        const handler = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeOnEscape();
            } else if (e.key === ' ' && !isVimeo) {
                e.preventDefault();
                if (videoRef.current) {
                    if (videoRef.current.paused) {
                        videoRef.current.play();
                    } else {
                        videoRef.current.pause();
                    }
                }
            }
        };

        window.addEventListener('keydown', handler);
        return () => {
            window.removeEventListener('keydown', handler);
        };
    }, [open, isVimeo]);

    const handlePlay = () => {
        if (videoRef.current) {
            videoRef.current.play();
        }
    };

    const handlePause = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };

    const dialogLabel = title || 'Video player';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            aria-labelledby="video-player-title"
            aria-describedby="video-player-description"
            role="dialog"
            className={className}
            data-testid={dataTestId}
            sx={{
                '& .MuiDialog-paper': {
                    margin: '0 !important',
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    maxHeight: '100% !important',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    overflow: 'hidden',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: `${color.black[70]} !important`,
                },
                '& .MuiDialogContent-root': {
                    padding: '0 !important',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogContent sx={{ padding: 0, position: 'relative' }}>
                <div id="video-player-title" aria-live="polite" aria-atomic="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    {dialogLabel}
                </div>
                <div id="video-player-description" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    {videoUrl ? 'Video player' : 'No video available'}
                </div>

                <Container>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,
                        }}
                    />
                    {videoUrl && (
                        <VideoContainer
                            onClick={(e) => e.stopPropagation()}
                            sx={{ position: 'relative', zIndex: 1 }}
                        >
                            {isVimeo ? (
                                <VimeoIframe
                                    src={`${videoUrl}${videoUrl.includes('?') ? '&' : '?'}autoplay=1`}
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                    allowFullScreen
                                    title={title || 'Video player'}
                                />
                            ) : (
                                <VideoElement
                                    ref={videoRef}
                                    src={videoUrl}
                                    controls
                                    autoPlay
                                    preload="auto"
                                    onPlay={handlePlay}
                                    onPause={handlePause}
                                    onEnded={handleEnded}
                                    aria-label={dialogLabel}
                                    role="application"
                                />
                            )}
                            <Controls>
                                <CloseButton disableRipple onClick={onClose} aria-label="Close video player">
                                    <IconClose color={color.white[0]} width={24} height={24} />
                                </CloseButton>
                            </Controls>
                        </VideoContainer>
                    )}
                </Container>
            </DialogContent>
        </Dialog>
    );
}
